import React from "react";
import {
 Card,
 Elevation,
 H3,
 H4,
 Button,
 Text,
 FormGroup,
 InputGroup,
 HTMLSelect,
 Callout,
 NonIdealState,
 Divider,
 Tag,
 Dialog,
 Classes as DialogClasses,
 TextArea,
 NumericInput,
} from "@blueprintjs/core";
import { DateInput } from "@blueprintjs/datetime";
import moment from "moment";
import { inject, observer } from "mobx-react";
import { apiCallPost } from "../../../../micro/fns/util";
import cogoToast from "cogo-toast";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine-dark.css";

class ConsumptionTab extends React.Component {
 gridApi = null;
 gridColumnApi = null;

 onGridReady = (params) => {
  this.gridApi = params.api;
  this.gridColumnApi = params.columnApi;
  const initialData = Array.isArray(this.props.itemConsumptions)
   ? this.props.itemConsumptions
   : [];
  console.log("ConsumptionTab: Grid ready with initial data:", initialData);
  this.gridApi.setRowData(initialData);
 };

 componentDidMount() {
  // Preload locations on mount to avoid waiting for dropdown interaction
  if (
   Array.isArray(this.props.availableLocations) &&
   this.props.availableLocations.length === 0
  ) {
   if (typeof this.props.onLoadLocations === "function") {
    this.props.onLoadLocations();
   }
  }
 }

 componentDidUpdate(prevProps) {
  console.log("ConsumptionTab componentDidUpdate:", {
   prevLength: prevProps.itemConsumptions?.length,
   currentLength: this.props.itemConsumptions?.length,
   hasGridApi: !!this.gridApi,
   propsChanged: prevProps.itemConsumptions !== this.props.itemConsumptions,
  });

  if (this.gridApi) {
   const rows = Array.isArray(this.props.itemConsumptions)
    ? this.props.itemConsumptions
    : [];

   console.log("ConsumptionTab: Updating grid with rows:", rows);
   console.log("ConsumptionTab: Row count:", rows.length);

   // Always update the grid data, not just when reference changes
   this.gridApi.setRowData(rows);

   // Force complete re-render of all cells, especially custom cellRenderers
   this.gridApi.redrawRows();

   // Recalculate row heights for autoHeight columns
   this.gridApi.resetRowHeights();

   // Ensure all changes are applied
   this.gridApi.refreshCells({ force: true, suppressFlash: false });
  }
 }
 // Create detailed audit logs for consumption creation
 createConsumptionAuditLogs = async () => {
  try {
   const entries = [];
   const {
    surgery,
    selectedConsumptionItems,
    consumptionForm,
    selectedLocation,
   } = this.props;

   const resourceId = surgery?.id || surgery?._id || surgery?.bookingId || "";
   const resourceName = `Surgery - ${
    surgery?.patientName || "Unknown Patient"
   }`;

   const safe = (v) => (v === null || v === undefined ? "" : v);

   // Per-item entries
   (selectedConsumptionItems || []).forEach((item) => {
    entries.push({
     resourceType: "SURGERY_BOOKING",
     resourceId,
     resourceName,
     actionType: "ADD_CONSUMPTION_ITEM",
     actionSlug: `Selected item for consumption: "${item.itemName}"`,
     actionMetadata: {
      fieldName: "consumption.items",
      changeType: "ADD",
      previousValue: "",
      newValue: item.itemName,
      itemDetails: {
       itemId: safe(item._id),
       itemName: safe(item.itemName),
       itemNameWithVariants: safe(item.itemNameWithVariants),
       stockItemSerialNumber: safe(item.stockItemSerialNumber),
       stockItemBarcode: safe(item.stockItemBarcode),
       stockItemExpiryDate: safe(item.stockItemExpiryDate || item.expiryDate),
       itemCategory: safe(item.itemCategory),
       remaining: safe(item.stockItemConsumptionsRemaining),
       branchName: safe(item.stockItemBranchName),
       locationName: safe(item.stockItemLocationName),
       brand: safe(item.itemBrand),
       model: safe(item.itemModel),
      },
     },
    });
   });

   // Aggregate entry for the consumption intent
   entries.push({
    resourceType: "SURGERY_BOOKING",
    resourceId,
    resourceName,
    actionType: "ADD_CONSUMPTION_ITEM",
    actionSlug: `Created consumption with ${
     (selectedConsumptionItems || []).length
    } item(s)`,
    actionMetadata: {
     fieldName: "consumption",
     changeType: "ADD",
     previousValue: "",
     newValue: "CREATED",
     details: {
      consumptionDate: safe(
       consumptionForm?.consumptionDate
        ? new Date(consumptionForm.consumptionDate).toISOString()
        : ""
      ),
      requestedConsumptions: safe(consumptionForm?.requestedConsumptions),
      consumingDoctorName: safe(consumptionForm?.consumingDoctorName),
      consumingPatientName: safe(consumptionForm?.consumingPatientName),
      consumptionHead: safe(consumptionForm?.consumptionHead),
      consumptionOT: safe(consumptionForm?.consumptionOT),
      notes: safe(consumptionForm?.notes),
      location: {
       id: safe(selectedLocation?._id),
       branchName: safe(selectedLocation?.branchName),
       locationName: safe(selectedLocation?.locationName),
      },
      itemCount: (selectedConsumptionItems || []).length,
     },
    },
   });

   if (entries.length > 0 && this.props.authStore?.prefix) {
    await apiCallPost(
     `/v3/${this.props.authStore.prefix}/neoSxAuditLog/createBulk`,
     entries
    );
   }
  } catch (err) {
   // Do not block UX on audit errors
   console.error("Consumption audit log failed:", err);
  }
 };

 handleCreateConsumptionClick = async () => {
  if (this.props.canEdit === false) {
   cogoToast.warn("Consumption is locked after discharge/cancellation");
   return;
  }
  const { selectedConsumptionItems, selectedLocation, consumptionForm } =
   this.props;

  // Client-side validation for required fields
  if (!selectedLocation || !selectedLocation._id) {
   cogoToast.error("Please select a location.");
   return;
  }
  if (
   !Array.isArray(selectedConsumptionItems) ||
   selectedConsumptionItems.length === 0
  ) {
   cogoToast.error("Please add at least one item to consume.");
   return;
  }
  if (!consumptionForm?.consumptionDate) {
   cogoToast.error("Please select a consumption date.");
   return;
  }
  if (
   !consumptionForm?.requestedConsumptions ||
   consumptionForm.requestedConsumptions < 1
  ) {
   cogoToast.error("Please enter requested consumptions (minimum 1).");
   return;
  }
  if (!consumptionForm?.consumptionHead) {
   cogoToast.error("Please select a consumption head.");
   return;
  }

  await this.createConsumptionAuditLogs();
  if (this.props.onCreateConsumption) {
   this.props.onCreateConsumption();
  }
 };
 // Format date for DateInput component
 formatDate = (date) => {
  if (!date) return "";
  return moment(date).format("DD/MM/YYYY");
 };

 // Parse date for DateInput component
 parseDate = (dateString) => {
  if (!dateString) return null;
  const parsed = moment(dateString, "DD/MM/YYYY");
  return parsed.isValid() ? parsed.toDate() : null;
 };

 // Theme helper for Blueprint dialogs
 getThemeClass = () => {
  try {
   const theme = document?.documentElement?.getAttribute("data-theme");
   return theme === "dark" ? "bp3-dark" : "";
  } catch (e) {
   return "";
  }
 };

 render() {
  const {
   surgery,
   itemConsumptions,
   consumptionSearchTerm,
   consumptionSearchResults,
   isSearchingConsumption,
   selectedConsumptionItems,
   selectedLocation,
   availableLocations,
   isLoadingLocations,
   showConsumptionDialog,
   consumptionForm,
   consumptionHeadOptions,
   isCreatingConsumption,
   onLocationChange,
   onSearchTermChange,
   onSearchStockItems,
   onLoadLocations,
   onClearSearchResults,
   onAddItemToConsumption,
   onRemoveItemFromConsumption,
   onOpenConsumptionDialog,
   onCloseConsumptionDialog,
   onUpdateConsumptionForm,
   onCreateConsumption,
  } = this.props;

  const isLocked = this.props.canEdit === false;

  return (
   <div style={{ padding: "20px" }}>
    <H3 style={{ marginBottom: "20px", color: "var(--text-primary)" }}>
     Items & Consumables
    </H3>

    {isLocked && (
     <Card
      elevation={Elevation.TWO}
      style={{
       backgroundColor: "#5c255c",
       marginBottom: "16px",
       padding: "10px 16px",
       display: "flex",
       alignItems: "center",
       gap: "10px",
      }}
     >
      <Tag intent="warning" icon="lock" large minimal>
       Locked
      </Tag>
      <Text style={{ color: "#f5f8fa" }}>
       Consumption fields are locked because this surgery has been discharged or cancelled.
      </Text>
     </Card>
    )}

    {/* Search Section */}
    <Card elevation={Elevation.ONE} style={{ marginBottom: "20px" }}>
     <div style={{ padding: "15px" }}>
      <H4 style={{ marginBottom: "15px", color: "var(--text-primary)" }}>
       Search Items for Consumption
      </H4>

      <fieldset disabled={isLocked} style={{ border: "none", padding: 0, margin: 0 }}>
      {/* Location Selection */}
      <div style={{ marginBottom: "15px" }}>
       <FormGroup label="Select Location *" labelFor="location">
        <HTMLSelect
         id="location"
         value={selectedLocation?._id || ""}
         onChange={(e) => {
          const location = availableLocations.find(
           (loc) => loc._id === e.target.value
          );
          onLocationChange(location);
          onClearSearchResults();
         }}
         onFocus={() => {
          if (availableLocations.length === 0 && !isLoadingLocations) {
           onLoadLocations();
          }
         }}
         fill
         style={{
          backgroundColor: "var(--bg-surface)",
          color: "var(--text-primary)",
         }}
         disabled={isLoadingLocations}
        >
         <option value="">
          {isLoadingLocations ? "Loading locations..." : "Select Location"}
         </option>
         {availableLocations.map((location) => (
          <option key={location._id} value={location._id}>
           {location.branchName} - {location.locationName}
          </option>
         ))}
        </HTMLSelect>
       </FormGroup>
      </div>

      {/* Search Input */}
      <div style={{ display: "flex", gap: "10px", marginBottom: "15px" }}>
       <InputGroup
        placeholder={
         selectedLocation
          ? "Enter barcode or serial number"
          : "Please select a location first"
        }
        value={consumptionSearchTerm}
        onChange={(e) => onSearchTermChange(e.target.value)}
        onKeyPress={(e) => e.key === "Enter" && onSearchStockItems()}
        style={{
         backgroundColor: "var(--bg-surface)",
         color: "var(--text-primary)",
        }}
        disabled={!selectedLocation}
       />
       <Button
        intent="primary"
        icon="search"
        onClick={onSearchStockItems}
        loading={isSearchingConsumption}
        disabled={!selectedLocation || !consumptionSearchTerm.trim()}
       >
        Search
       </Button>
      </div>

      {/* Location Selection Helper */}
      {!selectedLocation && (
       <Callout intent="warning" style={{ marginBottom: "15px" }}>
        <Text>
         Please select a location first to search for items. This will help
         narrow down the search to items available at that specific location.
        </Text>
       </Callout>
      )}

      {/* Search Results */}
      {consumptionSearchResults.length > 0 && (
       <div>
        <H4 style={{ marginBottom: "10px", color: "var(--text-primary)" }}>
         Search Results from {selectedLocation?.branchName} -{" "}
         {selectedLocation?.locationName}
        </H4>
        <div style={{ display: "grid", gap: "10px" }}>
         {consumptionSearchResults.map((item) => (
          <Card
           key={item._id}
           elevation={Elevation.ZERO}
           style={{ padding: "10px" }}
          >
           <div
            style={{
             display: "flex",
             justifyContent: "space-between",
             alignItems: "center",
            }}
           >
            <div>
             <Text style={{ fontWeight: "600", color: "var(--text-primary)" }}>
              {item.itemName}
             </Text>
             <Text style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
              {item.itemNameWithVariants}
             </Text>
             <div style={{ display: "flex", gap: "15px", marginTop: "5px" }}>
              <Text
               style={{ fontSize: "12px", color: "var(--text-secondary)" }}
              >
               <strong>Serial:</strong> {item.stockItemSerialNumber || "N/A"}
              </Text>
              {/* TODO: Add barcode */}
              <Text
               style={{ fontSize: "12px", color: "var(--text-secondary)" }}
              >
               <strong>Batch:</strong> {item.stockItemBatchNumber || "N/A"}
              </Text>
              <Text
               style={{ fontSize: "12px", color: "var(--text-secondary)" }}
              >
               <strong>Barcode:</strong> {item.stockItemBarcode || "N/A"}
              </Text>
              <Text
               style={{ fontSize: "12px", color: "var(--text-secondary)" }}
              >
               <strong>Category:</strong> {item.itemCategory}
              </Text>
              <Text
               style={{ fontSize: "12px", color: "var(--text-secondary)" }}
              >
               <strong>Remaining:</strong> {item.stockItemConsumptionsRemaining}
              </Text>
             </div>
             <div style={{ display: "flex", gap: "15px", marginTop: "5px" }}>
              <Text
               style={{ fontSize: "12px", color: "var(--text-secondary)" }}
              >
               <strong>Expiry:</strong>{" "}
               {item.stockItemExpiryDate
                ? moment(item.stockItemExpiryDate).format("DD/MM/YYYY")
                : item.expiryDate
                ? moment(item.expiryDate).format("DD/MM/YYYY")
                : "N/A"}
              </Text>
              <Text
               style={{ fontSize: "12px", color: "var(--text-secondary)" }}
              >
               <strong>Location:</strong> {item.stockItemBranchName} -{" "}
               {item.stockItemLocationName}
              </Text>
              {/* <Text
               style={{ fontSize: "12px", color: "var(--text-secondary)" }}
              >
               <strong>Brand:</strong> {item.itemBrand || "N/A"}
              </Text>
              <Text
               style={{ fontSize: "12px", color: "var(--text-secondary)" }}
              >
               <strong>Model:</strong> {item.itemModel || "N/A"}
              </Text> */}
             </div>
            </div>
            <Button
             intent="success"
             icon="add"
             onClick={() => onAddItemToConsumption(item)}
             disabled={!item.canConsume}
            >
             Add
            </Button>
           </div>
          </Card>
         ))}
        </div>
       </div>
      )}

      {/* Selected Items */}
      {selectedConsumptionItems.length > 0 && (
       <div style={{ marginTop: "20px" }}>
        <div
         style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "10px",
         }}
        >
         <H4 style={{ margin: 0, color: "var(--text-primary)" }}>
          Selected Items ({selectedConsumptionItems.length})
         </H4>
         {!isLocked && (
         <Button
          intent="primary"
          icon="confirm"
          onClick={onOpenConsumptionDialog}
         >
          Create Consumption
         </Button>
         )}
        </div>

        <div style={{ display: "grid", gap: "10px" }}>
         {selectedConsumptionItems.map((item) => (
          <Card
           key={item._id}
           elevation={Elevation.ZERO}
           style={{ padding: "10px" }}
          >
           <div
            style={{
             display: "flex",
             justifyContent: "space-between",
             alignItems: "flex-start",
            }}
           >
            <div style={{ flex: 1 }}>
             <Text style={{ fontWeight: "600", color: "var(--text-primary)" }}>
              {item.itemName}
             </Text>
             <Text
              style={{
               color: "var(--text-secondary)",
               fontSize: "14px",
               display: "block",
               marginTop: "2px",
              }}
             >
              {item.itemNameWithVariants}
             </Text>
             <div style={{ display: "flex", gap: "15px", marginTop: "5px" }}>
              <Text
               style={{ fontSize: "12px", color: "var(--text-secondary)" }}
              >
               <strong>Serial:</strong> {item.stockItemSerialNumber || "N/A"}
              </Text>
              <Text
               style={{ fontSize: "12px", color: "var(--text-secondary)" }}
              >
               <strong>Expiry:</strong>{" "}
               {item.stockItemExpiryDate
                ? moment(item.stockItemExpiryDate).format("DD/MM/YYYY")
                : item.expiryDate
                ? moment(item.expiryDate).format("DD/MM/YYYY")
                : "N/A"}
              </Text>
             </div>
            </div>
            <Button
             intent="danger"
             icon="trash"
             minimal
             onClick={() => onRemoveItemFromConsumption(item._id)}
            >
             Remove
            </Button>
           </div>
          </Card>
         ))}
        </div>
       </div>
      )}
      </fieldset>
     </div>
    </Card>

    {/* Existing Consumptions */}
    <Card elevation={Elevation.ONE}>
     <div style={{ padding: "15px" }}>
      <H4 style={{ marginBottom: "15px", color: "var(--text-primary)" }}>
       Consumption History
      </H4>

      {itemConsumptions.length > 0 ? (
       <div
        className="ag-theme-alpine-dark"
        style={{ height: 400, width: "100%" }}
       >
        <AgGridReact
         onGridReady={this.onGridReady}
         rowData={itemConsumptions}
         defaultColDef={{ resizable: true, sortable: true, filter: true }}
         columnDefs={[
          {
           headerName: "Reference",
           field: "consumptionNumber",
           width: 170,
           pinned: "left",
          },
          {
           headerName: "Date",
           valueGetter: (p) =>
            p.data?.consumptionDate
             ? moment(p.data.consumptionDate).format("DD/MM/YYYY HH:mm")
             : p.data?.createdAt
             ? moment(p.data.createdAt).format("DD/MM/YYYY HH:mm")
             : "",
           width: 180,
          },
          {
           headerName: "Requested",
           field: "requestedConsumptions",
           width: 130,
          },
          {
           headerName: "Consumed Items Details",
           cellRenderer: (params) => {
            const consumedItems = params.data?.consumedItems;

            // Handle case where consumedItems might be undefined, null, or empty
            if (!consumedItems) {
             return "Loading...";
            }

            if (!Array.isArray(consumedItems) || consumedItems.length === 0) {
             return "No items";
            }

            const container = document.createElement("div");
            container.style.padding = "5px 0";

            consumedItems.forEach((item, index) => {
             // Handle case where item might be just an ID string instead of an object
             if (typeof item === "string") {
              const itemDiv = document.createElement("div");
              itemDiv.style.marginBottom =
               index < consumedItems.length - 1 ? "8px" : "0";
              itemDiv.innerHTML = `
                <div style="font-weight: 600; color: var(--text-primary);">Item ID: ${item}</div>
                <div style="font-size: 11px; color: var(--text-secondary);">Loading details...</div>
              `;
              container.appendChild(itemDiv);
              return;
             }

             const itemDiv = document.createElement("div");
             itemDiv.style.marginBottom =
              index < consumedItems.length - 1 ? "8px" : "0";
             itemDiv.style.borderBottom =
              index < consumedItems.length - 1
               ? "1px solid rgba(255,255,255,0.1)"
               : "none";
             itemDiv.style.paddingBottom =
              index < consumedItems.length - 1 ? "8px" : "0";

             const itemName = item.itemName || item.name || "Unknown Item";
             const serialNumber =
              item.stockItemSerialNumber || item.serialNumber || "N/A";
             const expiryDate = item.stockItemExpiryDate || item.expiryDate;
             const expiryStr = expiryDate
              ? moment(expiryDate).format("DD/MM/YYYY")
              : "N/A";

             itemDiv.innerHTML = `
                            <div style="font-weight: 600; color: var(--text-primary);">${itemName}</div>
                            <div style="font-size: 11px; color: var(--text-secondary); margin-top: 2px;">
                              Serial: ${serialNumber} | Expiry: ${expiryStr}
                            </div>
                          `;
             container.appendChild(itemDiv);
            });

            return container;
           },
           width: 350,
           autoHeight: true,
           wrapText: true,
          },
          {
           headerName: "Count",
           valueGetter: (p) =>
            Array.isArray(p.data?.consumedItems)
             ? p.data.consumedItems.length
             : 0,
           width: 90,
          },
          { headerName: "Doctor", field: "consumingDoctorName", width: 180 },
          { headerName: "Patient", field: "consumingPatientName", width: 200 },
          { headerName: "Head", field: "consumptionHead", width: 140 },
          { headerName: "OT", field: "consumptionOT", width: 120 },
          { headerName: "Created By", field: "createdByName", width: 160 },
          { headerName: "Notes", field: "notes", flex: 1, minWidth: 200 },
         ]}
         getRowId={(params) =>
          params.data?._id ||
          params.data?.consumptionId ||
          params.data?.consumptionNumber ||
          `${params.data?.createdAt || ""}-${
           params.data?.requestedConsumptions || ""
          }`
         }
        />
       </div>
      ) : (
       <NonIdealState
        icon="box"
        title="No Items Consumed"
        description="No items have been consumed for this surgery yet"
       />
      )}
     </div>
    </Card>

    {/* Consumption Dialog */}
    <Dialog
     isOpen={showConsumptionDialog}
     onClose={onCloseConsumptionDialog}
     title="Create Item Consumption"
     className={this.getThemeClass()}
     style={{ width: "650px", maxWidth: "90vw" }}
    >
     <div
      className={DialogClasses.DIALOG_BODY}
      style={{
       backgroundColor: "var(--bg-primary)",
       color: "var(--text-primary)",
       padding: "20px",
      }}
     >
      <div style={{ marginBottom: "20px" }}>
       <H4 style={{ color: "var(--text-primary)", marginBottom: "10px" }}>
        Selected Items ({selectedConsumptionItems.length})
       </H4>
       <div
        style={{
         maxHeight: "150px",
         overflowY: "auto",
         border: "1px solid var(--border-color)",
         padding: "12px",
         borderRadius: "6px",
         backgroundColor: "var(--bg-surface)",
        }}
       >
        {selectedConsumptionItems.map((item, index) => (
         <div
          key={item._id}
          style={{
           padding: "10px 0",
           borderBottom:
            index < selectedConsumptionItems.length - 1
             ? "1px solid var(--border-color)"
             : "none",
          }}
         >
          <Text
           style={{
            fontWeight: "600",
            color: "var(--text-primary)",
            display: "block",
           }}
          >
           {item.itemName}
          </Text>
          <Text
           style={{
            color: "var(--text-secondary)",
            fontSize: "12px",
            display: "block",
            marginTop: "2px",
           }}
          >
           {item.itemNameWithVariants}
          </Text>
          <div style={{ display: "flex", gap: "15px", marginTop: "4px" }}>
           <Text style={{ fontSize: "11px", color: "var(--text-secondary)" }}>
            <strong>Serial:</strong> {item.stockItemSerialNumber || "N/A"}
           </Text>
           <Text style={{ fontSize: "11px", color: "var(--text-secondary)" }}>
            <strong>Expiry:</strong>{" "}
            {item.stockItemExpiryDate
             ? moment(item.stockItemExpiryDate).format("DD/MM/YYYY")
             : item.expiryDate
             ? moment(item.expiryDate).format("DD/MM/YYYY")
             : "N/A"}
           </Text>
          </div>
         </div>
        ))}
       </div>
      </div>

      <div
       style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "20px",
        marginBottom: "20px",
       }}
      >
       <FormGroup label="Consumption Date *" labelFor="consumptionDate">
        <DateInput
         value={consumptionForm.consumptionDate}
         onChange={(date) => onUpdateConsumptionForm("consumptionDate", date)}
         formatDate={this.formatDate}
         parseDate={this.parseDate}
         fill
         popoverProps={{
          className: this.getThemeClass(),
          popoverClassName: this.getThemeClass(),
         }}
         inputProps={{
          style: {
           backgroundColor: "var(--bg-surface)",
           color: "var(--text-primary)",
           borderColor: "var(--border-color)",
          },
         }}
        />
       </FormGroup>

       <FormGroup
        label="Requested Consumptions *"
        labelFor="requestedConsumptions"
       >
        <NumericInput
         id="requestedConsumptions"
         value={consumptionForm.requestedConsumptions}
         onValueChange={(value) =>
          onUpdateConsumptionForm("requestedConsumptions", value)
         }
         min={1}
         max={10}
         fill
         inputProps={{
          style: {
           backgroundColor: "var(--bg-surface)",
           color: "var(--text-primary)",
           borderColor: "var(--border-color)",
          },
         }}
        />
       </FormGroup>

       <FormGroup label="Consuming Doctor" labelFor="consumingDoctorName">
        <InputGroup
         id="consumingDoctorName"
         value={consumptionForm.consumingDoctorName}
         onChange={(e) =>
          onUpdateConsumptionForm("consumingDoctorName", e.target.value)
         }
         fill
         style={{
          backgroundColor: "var(--bg-surface)",
          color: "var(--text-primary)",
          borderColor: "var(--border-color)",
         }}
        />
        <Text
         style={{
          fontSize: "12px",
          color: "var(--text-secondary)",
          marginTop: "5px",
         }}
        >
         Doctor ID: {surgery?.doctorId || "N/A"}
        </Text>
       </FormGroup>

       <FormGroup label="Consuming Patient" labelFor="consumingPatientName">
        <InputGroup
         id="consumingPatientName"
         value={consumptionForm.consumingPatientName}
         onChange={(e) =>
          onUpdateConsumptionForm("consumingPatientName", e.target.value)
         }
         fill
         style={{
          backgroundColor: "var(--bg-surface)",
          color: "var(--text-primary)",
          borderColor: "var(--border-color)",
         }}
        />
        <Text
         style={{
          fontSize: "12px",
          color: "var(--text-secondary)",
          marginTop: "5px",
         }}
        >
         Patient ObjectId:{" "}
         {surgery?.patientOriginalData?._id || surgery?.patientId || "N/A"}
        </Text>
        <Text
         style={{
          fontSize: "12px",
          color: "var(--text-secondary)",
          marginTop: "2px",
         }}
        >
         Generic ID: {surgery?.patientGenericPid || "N/A"}
        </Text>
       </FormGroup>

       <FormGroup label="Consumption Head *" labelFor="consumptionHead">
        <HTMLSelect
         id="consumptionHead"
         value={consumptionForm.consumptionHead}
         onChange={(e) =>
          onUpdateConsumptionForm("consumptionHead", e.target.value)
         }
         fill
         style={{
          backgroundColor: "var(--bg-surface)",
          color: "var(--text-primary)",
          border: "1px solid var(--border-color)",
         }}
        >
         <option value="">Select consumption head</option>
         {consumptionHeadOptions.map((option) => (
          <option key={option} value={option}>
           {option}
          </option>
         ))}
        </HTMLSelect>
       </FormGroup>

       <FormGroup label="Consumption OT" labelFor="consumptionOT">
        <InputGroup
         id="consumptionOT"
         value={consumptionForm.consumptionOT}
         onChange={(e) =>
          onUpdateConsumptionForm("consumptionOT", e.target.value)
         }
         fill
         style={{
          backgroundColor: "var(--bg-surface)",
          color: "var(--text-primary)",
          borderColor: "var(--border-color)",
         }}
        />
       </FormGroup>
      </div>

      <FormGroup
       label="Notes"
       labelFor="consumptionNotes"
       style={{ marginTop: "10px" }}
      >
       <TextArea
        id="consumptionNotes"
        value={consumptionForm.notes}
        onChange={(e) => onUpdateConsumptionForm("notes", e.target.value)}
        fill
        rows={4}
        style={{
         backgroundColor: "var(--bg-surface)",
         color: "var(--text-primary)",
         borderColor: "var(--border-color)",
        }}
       />
      </FormGroup>
     </div>

     <div
      className={DialogClasses.DIALOG_FOOTER}
      style={{
       backgroundColor: "var(--bg-surface)",
       borderTop: "1px solid var(--border-color)",
       padding: "15px 20px",
      }}
     >
      <div className={DialogClasses.DIALOG_FOOTER_ACTIONS}>
       <Button onClick={onCloseConsumptionDialog}>Cancel</Button>
       <Button
        intent="primary"
        onClick={this.handleCreateConsumptionClick}
        loading={isCreatingConsumption}
       >
        Create Consumption
       </Button>
      </div>
     </div>
    </Dialog>
   </div>
  );
 }
}

export default inject("authStore")(observer(ConsumptionTab));
